import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { BellRinging, Eye, EyeSlash, ImageSquare, LockKey, Hash, ArrowUp, ArrowDown, NotePencil, Phone, Plus, Printer, SpinnerGap, Trash } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";

function PasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      setForm({ current: "", next: "", confirm: "" });
      toast.success("Mot de passe modifié");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  return (
    <form onSubmit={submit} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="password-card">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
        <LockKey size={20} weight="duotone" className="text-primary" /> Mot de passe de mon compte
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">Choisissez un mot de passe d'au moins 8 caractères.</p>
      <div className="mt-5 space-y-3">
        <input
          type="password"
          required
          autoComplete="current-password"
          value={form.current}
          onChange={(e) => setForm({ ...form, current: e.target.value })}
          placeholder="Mot de passe actuel"
          data-testid="password-current-input"
          className={inputCls}
        />
        <input
          type="password"
          required
          autoComplete="new-password"
          value={form.next}
          onChange={(e) => setForm({ ...form, next: e.target.value })}
          placeholder="Nouveau mot de passe"
          data-testid="password-new-input"
          className={inputCls}
        />
        <input
          type="password"
          required
          autoComplete="new-password"
          value={form.confirm}
          onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          placeholder="Confirmer le nouveau mot de passe"
          data-testid="password-confirm-input"
          className={inputCls}
        />
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid="password-save-button"
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border border-primary bg-white px-6 py-3.5 text-sm font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-60"
      >
        {saving && <SpinnerGap size={16} className="animate-spin" />}
        Changer le mot de passe
      </button>
    </form>
  );
}

function ReferenceCard() {
  const [value, setValue] = useState("");
  const [prefix, setPrefix] = useState("SIG");
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/reference")
      .then((r) => {
        setCurrent(r.data.ref_next);
        setValue(r.data.ref_next != null ? String(r.data.ref_next) : "");
        setPrefix(r.data.ref_prefix || "SIG");
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const n = parseInt(value, 10);
    if (!n || n < 1) {
      toast.error("Indiquez un numéro de départ valide (1 minimum)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/reference", { ref_next: n, ref_prefix: prefix });
      setCurrent(data.ref_next);
      setPrefix(data.ref_prefix);
      toast.success(`Prochaine référence : ${data.preview}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const cleanPrefix = (prefix || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
  const previewNum = parseInt(value, 10) >= 1 ? parseInt(value, 10) : current;
  const preview = previewNum != null ? `${cleanPrefix || "SIG"}-${String(previewNum).padStart(4, "0")}` : null;

  return (
    <form onSubmit={save} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="reference-card">
      <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
        <Hash size={20} weight="duotone" className="text-primary" /> Numérotation des signalements
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Choisissez le préfixe et le numéro de la prochaine référence — les suivantes s'incrémentent automatiquement. Laissez le numéro vide pour conserver les références aléatoires.
      </p>
      <div className="mt-5 flex items-center gap-3">
        <input
          type="text"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value.toUpperCase())}
          placeholder="SIG"
          maxLength={12}
          data-testid="reference-prefix-input"
          aria-label="Préfixe des références"
          className="w-28 shrink-0 rounded-2xl border border-black/10 px-4 py-3 text-sm uppercase outline-none transition-colors focus:border-primary"
        />
        <input
          type="number"
          min="1"
          max="999999"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Ex. 100"
          data-testid="reference-input"
          aria-label="Numéro de départ"
          className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
        />
        <span className="shrink-0 rounded-full bg-primary/10 px-4 py-2 font-mono text-xs font-bold text-primary" data-testid="reference-preview">
          {preview || "SIG-XXXXXX"}
        </span>
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid="reference-save-button"
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-primary bg-white px-6 py-3.5 text-sm font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-60"
      >
        {saving && <SpinnerGap size={16} className="animate-spin" />}
        Enregistrer la numérotation
      </button>
    </form>
  );
}


export default function AdminSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [serviceOptions, setServiceOptions] = useState([]);
  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setSettings((s) => ({ ...s, logo_url: data.path }));
      toast.success("Logo téléversé — pensez à enregistrer les réglages");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLogoUploading(false);
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/services")
      .then((r) => setServiceOptions((r.data || []).map((s) => ({ display: s.phone ? `${s.name} — ${s.phone}` : s.name, specialites: s.specialites || [] }))))
      .catch(() => {});
    api.get("/admin/settings").then((r) => setSettings({
      ...r.data,
      addresses_rows: (r.data.addresses || [r.data.building_address]).map((a) => a || ""),
      sub_buildings_rows: (r.data.sub_buildings || []).map((s) =>
        typeof s === "string"
          ? { name: s, elevator: "", chauffagiste: "", prestataire: "" }
          : { name: s.name || "", elevator: s.elevator || "", chauffagiste: s.chauffagiste || "", prestataire: s.prestataire || "" }
      ),
      garage_doors_rows: (r.data.garage_doors || []).map((d) =>
        typeof d === "string"
          ? { name: d, number: "", prestataire: "" }
          : { name: d.name || "", number: d.number || "", prestataire: d.prestataire || "" }
      ),
    })).catch((e) => toast.error(formatApiError(e)));
  }, [isAdmin]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const addresses = (settings.addresses_rows || []).map((a) => a.trim()).filter(Boolean);
      const { data } = await api.patch("/admin/settings", {
        building_name: settings.building_name,
        building_address: addresses[0] || settings.building_address,
        addresses: addresses.length ? addresses : [settings.building_address],
        address_city: settings.address_city || null,
        notify_email: settings.notify_email || null,
        concierge_copy: settings.concierge_copy || "all",
        feature_cleaning: settings.feature_cleaning !== false,
        feature_ratings: settings.feature_ratings !== false,
        urgency_enabled: settings.urgency_enabled !== false,
        sub_buildings: (settings.sub_buildings_rows || [])
          .map((r) => ({ name: r.name.trim(), elevator: r.elevator.trim() || null, chauffagiste: r.chauffagiste.trim() || null, prestataire: r.prestataire.trim() || null }))
          .filter((r) => r.name),
        garage_doors: (settings.garage_doors_rows || [])
          .map((r) => ({ name: r.name.trim(), number: r.number.trim() || null, prestataire: r.prestataire.trim() || null }))
          .filter((r) => r.name),
        reminder_days: settings.reminder_days || 15,
        cleaning_reminder_enabled: settings.cleaning_reminder_enabled === true,
        cleaning_reminder_days: settings.cleaning_reminder_days || 15,
        guardian_phone: settings.guardian_phone || null,
        logo_url: settings.logo_url || null,
        category_order: catOrder,
        hidden_categories: settings.hidden_categories || [],
        welcome_message: settings.welcome_message || null,
        welcome_enabled: settings.welcome_enabled !== false,
        announcement_text: settings.announcement_text || null,
        announcement_enabled: settings.announcement_enabled !== false,
        announcement_from: settings.announcement_from || null,
        announcement_until: settings.announcement_until || null,
        category_custom: settings.category_custom || {},
      });
      setSettings(data);
      toast.success("Réglages enregistrés");
      window.setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const updateSubRow = (i, field, value) =>
    setSettings((s) => ({
      ...s,
      sub_buildings_rows: s.sub_buildings_rows.map((r, j) => (j === i ? { ...r, [field]: value } : r)),
    }));
  const addSubRow = () =>
    setSettings((s) => ({ ...s, sub_buildings_rows: [...(s.sub_buildings_rows || []), { name: "", elevator: "", chauffagiste: "", prestataire: "" }] }));
  const removeSubRow = (i) =>
    setSettings((s) => ({ ...s, sub_buildings_rows: s.sub_buildings_rows.filter((_, j) => j !== i) }));
  const updateDoorRow = (i, field, value) =>
    setSettings((s) => ({
      ...s,
      garage_doors_rows: s.garage_doors_rows.map((r, j) => (j === i ? { ...r, [field]: value } : r)),
    }));
  const addDoorRow = () =>
    setSettings((s) => ({ ...s, garage_doors_rows: [...(s.garage_doors_rows || []), { name: "", number: "", prestataire: "" }] }));
  const removeDoorRow = (i) =>
    setSettings((s) => ({ ...s, garage_doors_rows: s.garage_doors_rows.filter((_, j) => j !== i) }));

  if (!isAdmin) {
    return (
      <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-settings-tab">
        <ReferenceCard />
        <PasswordCard />
      </div>
    );
  }

  if (!settings) {
    return <div className="h-40 animate-pulse rounded-3xl bg-black/5" data-testid="settings-loading" />;
  }

  const portalUrl = settings.building_id
    ? `${window.location.origin}/?b=${settings.building_id}`
    : window.location.origin;

  const inputCls =
    "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  const catOrder = (() => {
    const keys = (settings?.category_order || []).filter((k) => CATEGORIES.some((c) => c.key === k));
    CATEGORIES.forEach((c) => { if (!keys.includes(c.key)) keys.push(c.key); });
    return keys;
  })();

  const moveCategory = (key, dir) => {
    const i = catOrder.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= catOrder.length) return;
    const next = [...catOrder];
    [next[i], next[j]] = [next[j], next[i]];
    setSettings({ ...settings, category_order: next });
  };

  const toggleCategory = (key) => {
    const hidden = settings.hidden_categories || [];
    const next = hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key];
    setSettings({ ...settings, hidden_categories: next });
  };

  const providerSelect = (value, onChange, testid, spec) => {
    const opts = serviceOptions.filter((s) => !spec || s.specialites.includes(spec));
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={cn(inputCls, "min-w-52 flex-1")}
      >
        <option value="">Aucun prestataire</option>
        {value && !opts.some((o) => o.display === value) && <option value={value}>{value} (valeur actuelle)</option>}
        {opts.map((o) => (
          <option key={o.display} value={o.display}>{o.display}</option>
        ))}
        {opts.length === 0 && (
          <option value="" disabled>Aucun prestataire avec cette pastille — à cocher dans l'onglet Prestataires</option>
        )}
      </select>
    );
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-settings-tab">
      <div className="space-y-6">
      <form onSubmit={save} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h2 className="font-display text-lg font-bold tracking-tight">Copropriété active</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Ces informations s'affichent sur tout le portail résidents de cette copropriété.
        </p>
        <div className="mt-5 space-y-3">
          <div>
            <label htmlFor="settings-name" className="mb-1.5 block text-xs font-bold text-muted-foreground">Nom de la résidence</label>
            <input
              id="settings-name"
              value={settings.building_name}
              onChange={(e) => setSettings({ ...settings, building_name: e.target.value })}
              data-testid="settings-name-input"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold text-muted-foreground">Adresses de la résidence</label>
            <div className="space-y-2" data-testid="settings-addresses-list">
              <div>
                <input
                  value={settings.address_city || ""}
                  onChange={(e) => setSettings({ ...settings, address_city: e.target.value })}
                  placeholder="Code postal et ville (ex. 67000 Strasbourg)"
                  data-testid="settings-address-city-input"
                  className={inputCls}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Affiché sous l'adresse dans l'en-tête des emails.</p>
              </div>
              {(settings.addresses_rows || []).map((row, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`settings-address-row-${i}`}>
                  <input
                    value={row}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        addresses_rows: s.addresses_rows.map((r, j) => (j === i ? e.target.value : r)),
                      }))
                    }
                    placeholder={i === 0 ? "Adresse principale" : "Adresse supplémentaire"}
                    data-testid={`settings-address-input-${i}`}
                    className={inputCls}
                  />
                  {(settings.addresses_rows || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSettings((s) => ({ ...s, addresses_rows: s.addresses_rows.filter((_, j) => j !== i) }))
                      }
                      aria-label={`Retirer l'adresse ${i + 1}`}
                      data-testid={`settings-address-remove-${i}`}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-black/10 text-muted-foreground transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
                    >
                      <Trash size={16} weight="duotone" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setSettings((s) => ({ ...s, addresses_rows: [...(s.addresses_rows || []), ""] }))}
              data-testid="settings-address-add-button"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-xs font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <Plus size={14} weight="bold" /> Ajouter une adresse
            </button>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Plusieurs lignes possibles si la résidence s'étend sur plusieurs rues — elles s'affichent sur l'affiche et la page Infos.
            </p>
          </div>
          <div>
            <label htmlFor="settings-guardian-phone" className="mb-1.5 block text-xs font-bold text-muted-foreground">
              Numéro du concierge
            </label>
            <input
              id="settings-guardian-phone"
              value={settings.guardian_phone || ""}
              onChange={(e) => setSettings({ ...settings, guardian_phone: e.target.value })}
              placeholder="06 12 34 56 78"
              data-testid="settings-guardian-phone-input"
              className={inputCls}
            />
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Affiché sur l'affiche QR du hall (« Une urgence ? Concierge : … »). Laissez vide pour ne rien afficher.
            </p>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold text-muted-foreground">Logo de la résidence</label>
            <div className="flex items-center gap-3">
              {settings.logo_url ? (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-black/10 bg-white p-1.5">
                  <img
                    src={fileUrl(settings.logo_url)}
                    alt="Logo de la résidence"
                    data-testid="settings-logo-preview"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-dashed border-black/15 text-muted-foreground">
                  <ImageSquare size={22} weight="duotone" />
                </div>
              )}
              <div className="flex flex-col items-start gap-1.5">
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  data-testid="settings-logo-upload-button"
                  className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-xs font-bold transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
                >
                  {logoUploading ? <SpinnerGap size={13} className="animate-spin" /> : <Plus size={13} weight="bold" />}
                  {settings.logo_url ? "Remplacer le logo" : "Téléverser un logo"}
                </button>
                {settings.logo_url && (
                  <button
                    type="button"
                    onClick={() => setSettings({ ...settings, logo_url: null })}
                    data-testid="settings-logo-remove-button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-xs font-bold text-muted-foreground transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
                  >
                    <Trash size={12} weight="duotone" /> Retirer le logo
                  </button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Affiché en haut de l'affiche QR à la place du logo Ma Copropriété. PNG ou JPG, fond transparent conseillé. Enregistrez les réglages pour l'appliquer.
            </p>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              hidden
              data-testid="settings-logo-input"
              onChange={handleLogo}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold text-muted-foreground">Bâtiments de la résidence</label>
            <div className="space-y-2" data-testid="settings-sub-buildings-list">
              {(settings.sub_buildings_rows || []).map((row, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`settings-sub-building-row-${i}`}>
                  <input
                    value={row.name}
                    onChange={(e) => updateSubRow(i, "name", e.target.value)}
                    placeholder="Bâtiment (ex. Bâtiment A)"
                    data-testid={`settings-sub-name-${i}`}
                    className={cn(inputCls, "min-w-36 flex-1")}
                  />
                  <input
                    value={row.elevator}
                    onChange={(e) => updateSubRow(i, "elevator", e.target.value)}
                    placeholder="N° ascenseur"
                    data-testid={`settings-sub-elevator-${i}`}
                    className={cn(inputCls, "w-32")}
                  />
                  {providerSelect(row.chauffagiste, (v) => updateSubRow(i, "chauffagiste", v), `settings-sub-chauffagiste-${i}`, "chauffage")}
                  {providerSelect(row.prestataire, (v) => updateSubRow(i, "prestataire", v), `settings-sub-prestataire-${i}`, "ascenseur")}
                  <button
                    type="button"
                    onClick={() => removeSubRow(i)}
                    aria-label={`Retirer le bâtiment ${i + 1}`}
                    data-testid={`settings-sub-remove-${i}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-black/10 text-muted-foreground transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
                  >
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addSubRow}
              data-testid="settings-sub-add-button"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-xs font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <Plus size={14} weight="bold" /> Ajouter un bâtiment
            </button>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Le résident choisira obligatoirement l'un de ces bâtiments dans son signalement. Le n° d'ascenseur, le chauffagiste et le prestataire d'entretien (à choisir parmi les prestataires de l'onglet Prestataires) s'affichent automatiquement, téléphone cliquable. Laissez la liste vide si la résidence n'a qu'un seul bâtiment.
            </p>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold text-muted-foreground">Portes de garage de la résidence</label>
            <div className="space-y-2" data-testid="settings-garage-doors-list">
              {(settings.garage_doors_rows || []).map((row, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`settings-door-row-${i}`}>
                  <input
                    value={row.name}
                    onChange={(e) => updateDoorRow(i, "name", e.target.value)}
                    placeholder="Porte (ex. Porte parking)"
                    data-testid={`settings-door-name-${i}`}
                    className={cn(inputCls, "min-w-36 flex-1")}
                  />
                  <input
                    value={row.number}
                    onChange={(e) => updateDoorRow(i, "number", e.target.value)}
                    placeholder="N° de la porte"
                    data-testid={`settings-door-number-${i}`}
                    className={cn(inputCls, "w-32")}
                  />
                  {providerSelect(row.prestataire, (v) => updateDoorRow(i, "prestataire", v), `settings-door-prestataire-${i}`, "porte_garage")}
                  <button
                    type="button"
                    onClick={() => removeDoorRow(i)}
                    aria-label={`Retirer la porte ${i + 1}`}
                    data-testid={`settings-door-remove-${i}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-black/10 text-muted-foreground transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
                  >
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addDoorRow}
              data-testid="settings-door-add-button"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-xs font-bold transition-colors hover:border-primary hover:text-primary"
            >
              <Plus size={14} weight="bold" /> Ajouter une porte
            </button>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Pour un signalement « Porte de garage », le résident choisit la porte concernée ; son numéro et le prestataire d'entretien (choisi parmi les prestataires de l'onglet Prestataires) s'affichent automatiquement. Laissez la liste vide pour ne pas proposer de choix.
            </p>
          </div>
          <div>
            <label htmlFor="settings-email" className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-muted-foreground">
              <BellRinging size={14} weight="duotone" /> Email de notification (nouveaux signalements)
            </label>
            <input
              id="settings-email"
              type="email"
              value={settings.notify_email || ""}
              onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })}
              placeholder="votre@email.fr — laissez vide pour désactiver"
              data-testid="settings-email-input"
              className={inputCls}
            />
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Un email vous est envoyé à chaque nouveau signalement de résident.
            </p>
          </div>
          <div>
            <label htmlFor="settings-concierge-copy" className="mb-1.5 block text-xs font-bold text-muted-foreground">
              Copie cachée au concierge
            </label>
            <select
              id="settings-concierge-copy"
              value={settings.concierge_copy || "all"}
              onChange={(e) => setSettings({ ...settings, concierge_copy: e.target.value })}
              data-testid="settings-concierge-copy"
              className={inputCls}
            >
              <option value="all">Tous les signalements</option>
              <option value="proprete">Uniquement la propreté</option>
              <option value="off">Désactivée</option>
            </select>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              Envoyée au(x) concierge(s) dont l'email est renseigné dans l'onglet Contacts.
            </p>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-black/10 px-4 py-3" data-testid="settings-cleaning-reminder-row">
            <div>
              <p className="text-xs font-bold">Rappel automatique au prestataire de nettoyage</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Désactivé par défaut. Si activé, un email est envoyé chaque matin à 7h00 au(x) prestataire(s) coché(s) « Nettoyage » dans l'onglet Prestataires, une seule fois dès que le délai sans passage est dépassé.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.cleaning_reminder_enabled === true}
              data-testid="settings-cleaning-reminder-toggle"
              onClick={() => {
                const enabling = settings.cleaning_reminder_enabled !== true;
                setSettings({ ...settings, cleaning_reminder_enabled: enabling, ...(enabling ? { feature_cleaning: true } : {}) });
              }}
              className={cn(
                "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200",
                settings.cleaning_reminder_enabled === true ? "bg-primary" : "bg-black/15"
              )}
            >
              <span
                className={cn(
                  "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200",
                  settings.cleaning_reminder_enabled === true ? "left-6" : "left-1"
                )}
              />
            </button>
          </div>
          {settings.cleaning_reminder_enabled === true && (
            <div data-testid="settings-cleaning-reminder-options">
              <label htmlFor="settings-reminder-days" className="mb-1.5 block text-xs font-bold text-muted-foreground">
                Délai avant rappel (cette copropriété uniquement)
              </label>
              <select
                id="settings-reminder-days"
                value={settings.cleaning_reminder_days || 15}
                onChange={(e) => setSettings({ ...settings, cleaning_reminder_days: parseInt(e.target.value, 10) })}
                data-testid="settings-reminder-days"
                className={inputCls}
              >
                <option value={7}>Après 7 jours sans passage</option>
                <option value={15}>Après 15 jours sans passage</option>
                <option value={30}>Après 30 jours sans passage</option>
              </select>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Ce délai est propre à cette copropriété : vous pouvez choisir un rythme différent pour chaque immeuble.
              </p>
              {settings.cleaning_link && (
                <div className="mt-3 rounded-2xl border border-black/10 bg-black/[0.02] p-3" data-testid="settings-cleaning-link-box">
                  <p className="text-[11px] font-bold text-muted-foreground">
                    Lien de confirmation à transmettre au prestataire (aussi inclus dans l'email de rappel)
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={settings.cleaning_link}
                      data-testid="settings-cleaning-link"
                      className="min-w-0 flex-1 rounded-xl border border-black/10 bg-white px-3 py-2 text-[11px] text-muted-foreground outline-none"
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      data-testid="settings-cleaning-link-copy"
                      onClick={() => {
                        navigator.clipboard.writeText(settings.cleaning_link);
                        toast.success("Lien copié — transmettez-le au prestataire");
                      }}
                      className="shrink-0 rounded-xl bg-primary px-4 py-2 text-[11px] font-bold text-primary-foreground transition-colors hover:bg-[#265442]"
                    >
                      Copier
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-black/10 px-4 py-3" data-testid="settings-cleaning-row">
            <div>
              <p className="text-xs font-bold">Suivi d'entretien</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Affiche la page Entretien et permet de noter les passages de nettoyage.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.feature_cleaning !== false}
              data-testid="settings-cleaning-toggle"
              onClick={() => setSettings({ ...settings, feature_cleaning: settings.feature_cleaning === false })}
              className={cn(
                "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200",
                settings.feature_cleaning !== false ? "bg-primary" : "bg-black/15"
              )}
            >
              <span
                className={cn(
                  "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200",
                  settings.feature_cleaning !== false ? "left-6" : "left-1"
                )}
              />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-black/10 px-4 py-3" data-testid="settings-ratings-row">
            <div>
              <p className="text-xs font-bold">Notes et avis des prestataires</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Permet aux résidents de noter les prestataires en étoiles et de laisser un avis sur la page Services.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.feature_ratings !== false}
              data-testid="settings-ratings-toggle"
              onClick={() => setSettings({ ...settings, feature_ratings: settings.feature_ratings === false })}
              className={cn(
                "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200",
                settings.feature_ratings !== false ? "bg-primary" : "bg-black/15"
              )}
            >
              <span
                className={cn(
                  "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200",
                  settings.feature_ratings !== false ? "left-6" : "left-1"
                )}
              />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-black/10 px-4 py-3" data-testid="settings-urgency-row">
            <div>
              <p className="text-xs font-bold">Bandeau URGENCE dans les emails prestataires</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Affiche le bandeau rouge et l'objet « URGENT — » quand un signalement est marqué urgent. Désactivez pour ne plus jamais l'afficher.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings.urgency_enabled !== false}
              data-testid="settings-urgency-toggle"
              onClick={() => setSettings({ ...settings, urgency_enabled: settings.urgency_enabled === false })}
              className={cn(
                "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200",
                settings.urgency_enabled !== false ? "bg-primary" : "bg-black/15"
              )}
            >
              <span
                className={cn(
                  "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200",
                  settings.urgency_enabled !== false ? "left-6" : "left-1"
                )}
              />
            </button>
          </div>
          <div className="rounded-2xl border border-black/10 px-4 py-3" data-testid="settings-welcome-message">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-bold">Message d'accueil de la page « Signaler »</p>
              <button
                type="button"
                role="switch"
                aria-checked={settings.welcome_enabled !== false}
                onClick={() => setSettings({ ...settings, welcome_enabled: settings.welcome_enabled === false })}
                data-testid="settings-welcome-toggle"
                aria-label="Activer ou désactiver le message d'accueil"
                className={cn(
                  "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200",
                  settings.welcome_enabled !== false ? "bg-primary" : "bg-black/15"
                )}
              >
                <span
                  className={cn(
                    "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200",
                    settings.welcome_enabled !== false ? "left-6" : "left-1"
                  )}
                />
              </button>
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Affiché en haut du formulaire de signalement pour guider les résidents. Désactivez l'interrupteur ou laissez vide pour ne rien afficher.
            </p>
            <textarea
              value={settings.welcome_message || ""}
              onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
              rows={3}
              maxLength={500}
              placeholder="Ex. : En cas d'urgence (fuite importante, coupure totale), appelez d'abord le gardien."
              data-testid="settings-welcome-message-input"
              className="mt-2 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
            />
          </div>
          <div className="rounded-2xl border border-black/10 px-4 py-3" data-testid="settings-announcement">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-bold">Bandeau d'annonce du portail</p>
              <button
                type="button"
                role="switch"
                aria-checked={settings.announcement_enabled !== false}
                onClick={() => setSettings({ ...settings, announcement_enabled: settings.announcement_enabled === false })}
                data-testid="settings-announcement-toggle"
                aria-label="Activer ou désactiver le bandeau d'annonce"
                className={cn(
                  "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200",
                  settings.announcement_enabled !== false ? "bg-primary" : "bg-black/15"
                )}
              >
                <span
                  className={cn(
                    "absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200",
                    settings.announcement_enabled !== false ? "left-6" : "left-1"
                  )}
                />
              </button>
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Affiché en haut de toutes les pages du portail résidents (ex. travaux prévus). Désactivez l'interrupteur ou laissez vide pour le masquer.
            </p>
            <textarea
              value={settings.announcement_text || ""}
              onChange={(e) => setSettings({ ...settings, announcement_text: e.target.value })}
              rows={2}
              maxLength={500}
              placeholder="Ex. : Travaux de ravalement prévus du 20 au 24 octobre, merci de votre compréhension."
              data-testid="settings-announcement-input"
              className="mt-2 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
            />
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="settings-announcement-from" className="mb-1 block text-[11px] font-bold text-muted-foreground">
                  Date de début (optionnel) — affichage automatique à partir de ce jour
                </label>
                <input
                  id="settings-announcement-from"
                  type="date"
                  value={settings.announcement_from || ""}
                  onChange={(e) => setSettings({ ...settings, announcement_from: e.target.value })}
                  data-testid="settings-announcement-from-input"
                  className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="settings-announcement-until" className="mb-1 block text-[11px] font-bold text-muted-foreground">
                  Date de fin (optionnel) — disparition automatique après cette date
                </label>
                <input
                  id="settings-announcement-until"
                  type="date"
                  value={settings.announcement_until || ""}
                  onChange={(e) => setSettings({ ...settings, announcement_until: e.target.value })}
                  data-testid="settings-announcement-until-input"
                  className="w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
                />
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-black/10 px-4 py-3" data-testid="settings-category-order">
            <p className="text-xs font-bold">Ordre des rubriques de signalement</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              L'ordre choisi ici s'applique à la page « Signaler » pour les résidents. L'œil masque une rubrique, le crayon la renomme et lui ajoute une consigne.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {catOrder.map((key, i) => {
                const cat = CATEGORIES.find((c) => c.key === key);
                if (!cat) return null;
                const Icon = cat.Icon;
                const isHidden = (settings.hidden_categories || []).includes(key);
                const customCat = (settings.category_custom || {})[key] || {};
                const hasCustom = Boolean((customCat.label || "").trim() || (customCat.message || "").trim());
                return (
                  <div key={key} className={cn("flex items-center gap-2 rounded-xl bg-black/[0.03] px-3 py-2 transition-opacity", isHidden && "opacity-50")} data-testid={`category-order-${key}`}>
                    <Icon size={15} weight="duotone" className="shrink-0 text-primary" />
                    <span className={cn("min-w-0 flex-1 truncate text-xs font-bold", isHidden && "line-through")}>{customCat.label || cat.label}</span>
                    <button
                      type="button"
                      onClick={() => setEditingCat(editingCat === key ? null : key)}
                      data-testid={`category-customize-${key}`}
                      aria-label={`Personnaliser ${cat.label}`}
                      title="Personnaliser le libellé et la consigne"
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors",
                        hasCustom
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-black/10 text-foreground/50 hover:border-primary hover:text-primary"
                      )}
                    >
                      <NotePencil size={12} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleCategory(key)}
                      data-testid={`category-toggle-${key}`}
                      aria-label={isHidden ? `Afficher ${cat.label}` : `Masquer ${cat.label}`}
                      title={isHidden ? "Afficher cette rubrique" : "Masquer cette rubrique"}
                      className={cn(
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors",
                        isHidden
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-black/10 text-foreground/50 hover:border-primary hover:text-primary"
                      )}
                    >
                      {isHidden ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCategory(key, -1)}
                      disabled={i === 0}
                      data-testid={`category-up-${key}`}
                      aria-label={`Monter ${cat.label}`}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-primary hover:text-primary disabled:opacity-30"
                    >
                      <ArrowUp size={12} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCategory(key, 1)}
                      disabled={i === catOrder.length - 1}
                      data-testid={`category-down-${key}`}
                      aria-label={`Descendre ${cat.label}`}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-primary hover:text-primary disabled:opacity-30"
                    >
                      <ArrowDown size={12} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>
            {editingCat && (() => {
              const cat = CATEGORIES.find((c) => c.key === editingCat);
              if (!cat) return null;
              const customCat = (settings.category_custom || {})[editingCat] || {};
              const updateCustom = (field, value) => {
                const all = { ...(settings.category_custom || {}) };
                const entry = { ...all[editingCat], [field]: value };
                if (!(entry.label || "").trim() && !(entry.message || "").trim()) {
                  delete all[editingCat];
                } else {
                  all[editingCat] = entry;
                }
                setSettings({ ...settings, category_custom: all });
              };
              return (
                <div className="mt-2 rounded-xl border border-primary/20 bg-primary/[0.03] px-3 py-3" data-testid="category-custom-editor">
                  <p className="text-xs font-bold">Personnaliser « {cat.label} »</p>
                  <input
                    type="text"
                    value={customCat.label || ""}
                    onChange={(e) => updateCustom("label", e.target.value)}
                    placeholder={`Libellé personnalisé (défaut : ${cat.label})`}
                    maxLength={40}
                    data-testid="category-custom-label-input"
                    className="mt-2 w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-xs outline-none transition-colors focus:border-primary"
                  />
                  <textarea
                    value={customCat.message || ""}
                    onChange={(e) => updateCustom("message", e.target.value)}
                    placeholder="Consigne affichée au résident quand il choisit cette rubrique (ex. numéro du dépanneur) — optionnel"
                    rows={2}
                    maxLength={500}
                    data-testid="category-custom-message-input"
                    className="mt-2 w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-xs outline-none transition-colors focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setEditingCat(null)}
                    data-testid="category-custom-close"
                    className="mt-2 rounded-full border border-black/10 px-4 py-1.5 text-xs font-bold transition-colors hover:border-primary hover:text-primary"
                  >
                    Fermer
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="settings-save-button"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
        >
          {saving && <SpinnerGap size={16} className="animate-spin" />}
          Enregistrer les réglages
        </button>
      </form>
      <ReferenceCard />
      <PasswordCard />
      </div>

      <div>
        <div
          id="qr-poster"
          className="noise relative overflow-hidden rounded-3xl bg-[#18382B] px-8 py-12 text-center text-[#F7F5F0] shadow-[0_18px_50px_rgba(24,56,43,0.35)]"
          data-testid="qr-poster"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full border border-[#F7F5F0]/10" />
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full border border-[#F7F5F0]/10" />
          <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full border border-[#F7F5F0]/10" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-40 w-40 rounded-full bg-[#7FC8A9]/10" />

          <div className="relative flex items-center justify-center gap-3">
            {settings.logo_url ? (
              <span className="rounded-2xl bg-[#F7F5F0] p-3">
                <img
                  src={fileUrl(settings.logo_url)}
                  alt="Logo de la résidence"
                  data-testid="poster-building-logo"
                  className="h-12 w-auto object-contain"
                />
              </span>
            ) : (
              <>
                <Logo size={40} />
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#F7F5F0]/70">Ma Copropriété</p>
              </>
            )}
          </div>

          <p className="relative mt-10 font-display text-3xl font-extrabold leading-[1.15] tracking-[-0.02em]">
            Un problème dans<br />la copropriété ?
          </p>
          <p className="relative mt-3 text-sm text-[#F7F5F0]/70">
            Signalez-le en 2 minutes, sans compte.
          </p>

          <div className="relative mx-auto mt-8 w-fit rounded-3xl bg-[#F7F5F0] p-5 shadow-[0_12px_40px_rgba(0,0,0,0.35)]">
            <QRCode value={portalUrl} size={170} fgColor="#18382B" bgColor="transparent" />
          </div>
          <p className="relative mt-5 text-xs font-bold uppercase tracking-[0.25em] text-[#7FC8A9]">
            Scannez avec votre téléphone
          </p>

          <div className="relative mx-auto mt-8 flex max-w-xs flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] font-semibold text-[#F7F5F0]/60">
            <span>Signalements</span>
            {settings.feature_cleaning !== false && (
              <>
                <span className="text-[#7FC8A9]">•</span>
                <span>Suivi entretien</span>
              </>
            )}
            <span className="text-[#7FC8A9]">•</span>
            <span>Services.contacts</span>
          </div>
          {settings.guardian_phone && (
            <div className="relative mt-6" data-testid="poster-guardian-phone">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#F7F5F0]/20 px-4 py-2 text-xs font-bold text-[#F7F5F0]">
                <Phone size={14} weight="duotone" className="text-[#7FC8A9]" />
                Une urgence ? Concierge : {settings.guardian_phone}
              </span>
            </div>
          )}

          <div className="relative mt-8 border-t border-[#F7F5F0]/15 pt-5">
            <p className="font-display text-base font-bold">{settings.building_name}</p>
            {(settings.addresses_rows || [settings.building_address]).filter((a) => a && a.trim()).map((a, i) => (
              <p key={i} className="mt-1 text-xs text-[#F7F5F0]/60">{a}</p>
            ))}
          </div>
        </div>
        <button
          onClick={() => window.print()}
          data-testid="qr-print-button"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-6 py-3.5 text-sm font-bold transition-colors hover:border-primary hover:text-primary"
        >
          <Printer size={16} weight="duotone" />
          Imprimer l'affiche pour le hall
        </button>
      </div>
    </div>
  );
}
